import React, { useState, useRef, useCallback, useEffect, SyntheticEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Accordion, AccordionSummary, AccordionDetails, Grid } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Typography, Box, Stack, Button, useFinanceableTheme } from "@financeable-com-au/financeable-ui";
import { debounce } from "lodash";
import * as Yup from "yup";
import moment from "moment";
import {
  ApplicationAssetSections,
  ApplicationAssetSectionsList,
  ASSET_LOOKUP_PROVIDERS,
  ASSET_SEARCH_OPTIONS,
  DEFAULT_EXPANDED_ASSET_SECTIONS,
  ERRORS_MESSAGE,
  fieldRequiredMessage,
  INSURANCE_QUOTE_ASSETS,
  PLAIN_TEXT_VEHICLE_SEARCH,
  searchableAssets,
  SUCCESS_MESSAGE,
  VEHICLE_LOOKUP_SEARCH,
} from "@/constants";
import {
  assetLookupProviderSelector,
  assetLookupProviderSettledSelector,
  getAssetLookupProvider,
  getDetailsSpecificationAll,
  getListOfMakes,
  getListOfMakesYearFromTo,
  getListOfModels,
  getListOfModelsYearFromTo,
  getListOfNVICForAdvancedSearch,
  getListOfVariants,
  getListOfVariantsYearFromTo,
  getVehicleLookup,
  getVehiclePPSRSearch,
  getVehicleSpecification,
  loanDetailsSelector,
  searchVehiclesByKeyword,
  setValidationRequireFieldError,
  updateAgeOfAsset,
  updateAsset,
  updateGlassAsset,
  userDetailsSelector,
  setAssetExpandedSection,
  // @ts-expect-error: ignoring as it is existing js file
} from "@/store/slices/applicationFormSlice";
import { usePaidServices } from "@/hooks";
// @ts-expect-error: ignoring as it is existing js file
import { userPermissionsSelector } from "@/store/slices/userSlice";
// @ts-expect-error: ignoring as it is existing js file
import consumerAssetTypes from "@/utils/consumerAssetTypes";
import { useGetLocalAndLenderErrors, useShowRequiredFields } from "@/hooks/useRequiredFields";
// @ts-expect-error: ignoring as it is existing js file
import regex from "@/utils/regex";
// @ts-expect-error: ignoring as it is existing js file
import { isValidInput } from "@/components/Utils/helperFunction";
import { useCalculateApplication } from "@/hooks/useCalculateApplication/useCalculateApplication";
import { fCurrency } from "@/utils/formatNumber";
import { getErrorToastPayload, getSuccessToastPayload } from "@/utils/commonFunctions";
import {
  AddOnsProps,
  AssetSectionsProps,
  AssetTypesJsonProps,
  GetLocalAndLenderErrorProps,
  IDispatchResponse,
} from "@/utils/interface";
import {
  ApplicationState,
  Asset,
  Specifications,
  LoanDetails,
  Customer,
  ApplicationType,
  UserDetailsState,
} from "@/store/slices/types/applicationFormSlice";
import { useFeatureFlags } from "@/utils/featureFlags";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { useAccessAppComponent } from "@/hooks/useAccessAppComponent";
import { Loader } from "@/components/Loader";
import AssetActions from "./AssetActions";
import AssetInvoice from "./AssetInvoice";
import SearchVehicle from "./SearchVehicle";
import PlainTextVehicleSearch from "./PlainTextVehicleSearch";
import AssetMakeModelSearch from "./AssetMakeModelSearch";
import AssetSearchOptions, { AssetSearchOption } from "./AssetSearchOptions";
import AssetDealDetails from "./AssetDealDetails";
import GlassesGuideFields from "./GlassesGuideFields";
import Odometer from "./Odometer";
import AssetAddOns from "./AssetAddOns";
import GlassesGuideDetails from "./GlassesGuideDetails";
import AssetSpecificationDetails from "./AssetSpecificationDetails";
import AssetSupplier from "./AssetSupplier";
import InsuranceQuote from "./InsuranceQuote";
import { isGlassesGuideAssetMode } from "./assetRequiredFields";
import { useCascadeListFetch } from "./useCascadeListFetch";
import assetTypes from "../../assetTypes.json";

const advancedActionSectionIds = [
  ApplicationAssetSections.ASSET_DETAILS,
  ApplicationAssetSections.ADDONS,
  ApplicationAssetSections.INVOICE,
  ApplicationAssetSections.SUPPLIER,
  ApplicationAssetSections.INSURANCE_QUOTE,
];

const nestedSectionFields = [
  { section: ApplicationAssetSections.ODOMETER, fields: ["odometer", "serialNumber"] },
  {
    section: ApplicationAssetSections.SUPPLIER,
    fields: ["supplierName", "supplierContactName", "supplierEmail", "supplierPhone", "supplierABN", "supplierAddress"],
  },
];

type VehicleLookupQuery = { vin?: string; registrationNumber?: string; registrationState?: string };

/** Plate and VIN are hand-typed, so every value is URL-encoded. */
export const buildVehicleLookupQuery = (query: VehicleLookupQuery, applicationId: string, assetId: string) => {
  const param = (name: string, value?: string) => `${name}=${encodeURIComponent(value ?? "")}`;

  const identifier = query.vin
    ? param("vin", query.vin.replace(/\s+/g, ""))
    : `${param("plate", query.registrationNumber?.replace(/\s+/g, ""))}&${param("state", query.registrationState)}`;

  return `${identifier}&${param("applicationId", applicationId)}&${param("assetId", assetId)}`;
};

/** Fallback range used when no single year is selected. */
const LIST_YEAR_FROM = "1961";

/** Keeps the range in step with the picker's year list. */
const listYearTo = () => String(new Date().getFullYear() + 1);

/** Identifies the vehicle a specification describes, so the same one is not applied twice. */
export const getSpecificationKey = (specification: Record<string, unknown> | null | undefined) => {
  if (!specification || typeof specification !== "object") return null;

  const key = ["vehicleId", "NVIC_CUR", "NVIC", "ModelName", "YearCreate"]
    .map((field) => specification[field])
    .filter((value) => typeof value === "string" || typeof value === "number")
    .join("|");

  return key || null;
};

/** Only a plate or VIN lookup returns a VIN. */
export const readSpecificationVin = (specification: Record<string, unknown> | null | undefined) => {
  const vin = specification?.VIN;
  return typeof vin === "string" && vin.trim() !== "" ? vin.trim() : undefined;
};

const toGlassesOption = (field?: { label?: string; value?: string } | string | null) => {
  if (typeof field === "string") return { label: field, value: field };

  return field ? { label: field.label ?? "", value: field.value ?? "" } : { label: "", value: "" };
};

const vehicleLookupResultSnackbar = (res: IDispatchResponse) =>
  res?.error
    ? getErrorToastPayload(res, ERRORS_MESSAGE.fetchErrorMsg)
    : getSuccessToastPayload(res, SUCCESS_MESSAGE.fetchMsg);

const AssetSections = ({
  index,
  asset,
  applicationType,
  applicationId,
  isDisableEdit,
  hideAdvancedActions = false,
}: AssetSectionsProps) => {
  const dispatch = useDispatch();
  const theme = useFinanceableTheme();
  const { glassesNvicUpdate, isAssetInsurance } = useFeatureFlags();
  const { canUsePaidServices } = usePaidServices();
  const userPermissions = useSelector(userPermissionsSelector) as
    | { services?: { blug_flag?: boolean; blug_flag_ppsr?: boolean } }
    | undefined;
  const servicePermission = userPermissions?.services;
  const [assetExpanded, setAssetExpanded] = useState<null | string>(index === 0 ? asset?._id : null);
  const loanDetails = useSelector(loanDetailsSelector) as LoanDetails | null | undefined;
  const term = loanDetails?.term;
  const assetLookupProvider = useSelector(assetLookupProviderSelector) as string;
  const isAutograbLookup = assetLookupProvider === ASSET_LOOKUP_PROVIDERS.AUTOGRAB;
  // The provider arrives from the server, so until it does `assetLookupProvider` is only the default.
  const isAssetLookupProviderSettled = useSelector(assetLookupProviderSettledSelector) as boolean;
  const [showSearchAssetView, setShowSearchAssetView] = useState<string>("search");
  const userDetails = useSelector(userDetailsSelector) as
    | { assetErrors?: unknown; application?: ApplicationState }
    | undefined;
  const { assetExpandedSections, validationRequireFieldError } = userDetails as UserDetailsState;
  const assetValidationErrors = validationRequireFieldError?.assetErrors;
  const assetErrors = userDetails?.assetErrors;
  const firstCustomer = userDetails?.application?.customers?.[0] as Customer;
  const { resetInvalidRequiredFieldsByFieldName } = useShowRequiredFields();
  const isCommercialApplication = applicationType === ApplicationType.Commercial;
  const assetTypesList = isCommercialApplication ? assetTypes.assetTypes : consumerAssetTypes;
  const { getNetAssetValueProvidedSingleField } = useCalculateApplication();
  const { getAssetOrLoanDetailsLocalAndLenderError } = useGetLocalAndLenderErrors();
  const { accessComponents } = useAccessAppComponent();
  const {
    application: {
      asset: { isDefaultManualAssets, isVehicleSearch, isPPSRSearch, isGlassesGuideSearchable },
    },
  } = accessComponents;

  const getLocalAndLenderError = ({ fieldName, localFieldName, fieldError }: GetLocalAndLenderErrorProps) => {
    const filteredState = Object.fromEntries(
      Object.entries(allValues).filter(
        ([_, value]) =>
          typeof value === "string" ||
          (typeof value === "object" && value !== null && ("label" in value || "value" in value)),
      ),
    );
    return getAssetOrLoanDetailsLocalAndLenderError({
      fieldName,
      localFieldName,
      fieldError,
      localState: filteredState,
    });
  };

  let updatedApplicationAssetSectionsList = ApplicationAssetSectionsList;

  if (!isAssetInsurance()) {
    updatedApplicationAssetSectionsList = ApplicationAssetSectionsList.filter(
      (section) => section.id !== ApplicationAssetSections.INSURANCE_QUOTE,
    );
  }

  if (!isCommercialApplication) {
    updatedApplicationAssetSectionsList = ApplicationAssetSectionsList.filter(
      (section) => section.id !== ApplicationAssetSections.INSURANCE_QUOTE,
    );
  }

  if (hideAdvancedActions) {
    updatedApplicationAssetSectionsList = updatedApplicationAssetSectionsList.filter(
      (section) => !advancedActionSectionIds.includes(section.id),
    );
  }

  const [assetFieldErrors, setAssetFieldErrors] = useState<Record<string, string>>({
    assetValue: "",
    typeOfSale: "",
    condition: "",
    make: "",
    model: "",
    glassesMake: "",
    glassesModel: "",
    glassesVariant: "",
    glassesNVIC: "",
    glassesData: "",
    registrationNumberOrVin: "",
  });

  const {
    _id,
    assetValue,
    ageOfAsset = "",
    assetType = "",
    make = "",
    model = "",
    typeOfSale = "",
    condition = "",
    specifications,
    NVICData,
    glassesMake = { label: "", value: "" },
    glassesModel = {
      label: "",
      value: "",
    },
    glassesNVIC = { label: "", value: "" },
    glassesVariant = { label: "", value: "" },
    registrationState,
    registrationNumber,
    vin,
    vehicleData,
    isManualAssets,
    addOns = [],
    odometer = "",
    serialNumber = "",
    supplierName = "",
    supplierABN = "",
    supplierAddress = "",
    supplierPhone = "",
    supplierContactName = "",
    supplierEmail = "",
  } = asset;

  // Expansion is tracked per asset id.
  const expandedSections = assetExpandedSections?.[_id] ?? DEFAULT_EXPANDED_ASSET_SECTIONS;

  const [allValues, setAllValue] = useState({
    condition: condition || "",
    glassesData: {},
    specifications: specifications?.YearCreate ? specifications : NVICData || null,
    assetValue: assetValue || "",
    assetModelTypeCode: "",
    assetTypeValue: assetType || "",
    ageOfAsset: ageOfAsset || "",
    make: make || "",
    model: model || "",
    glassesMake: glassesMake,
    glassesModel: glassesModel,
    glassesVariant: glassesVariant || { label: "", value: "" },
    glassesNVIC: glassesNVIC,
    typeOfSale: typeOfSale || "",
    registrationState: registrationState || "",
    registrationNumber: registrationNumber || null,
    vin: vin || null,
    searchQuery: registrationNumber || vin || "",
    isManualAssets: isManualAssets || false,
    addOns: addOns || [],
    odometer: odometer || "",
    serialNumber: serialNumber || "",
    supplierName: supplierName || "",
    supplierContactName: supplierContactName || "",
    supplierABN: supplierABN || "",
    supplierAddress: supplierAddress || "",
    supplierPhone: supplierPhone || "",
    supplierEmail: supplierEmail || "",
    listOfModels: [],
    listOfVariants: [],
    NVICList: [],
  });

  const allValuesRef = useRef(allValues);

  const previousNestedErrors = useRef({
    assetId: _id,
    fields: new Set<string>(),
    validationErrors: assetValidationErrors,
  });

  // Compare field transitions on every render: validation, local values and section visibility can all change.
  // Show Fields publishes a fresh asset-error map even for identical errors. Reveal those again,
  // while expansion changes and ordinary rerenders still respect manual collapse.
  useEffect(() => {
    const validationTriggered = previousNestedErrors.current.validationErrors !== assetValidationErrors;
    const previousFields =
      previousNestedErrors.current.assetId === _id ? previousNestedErrors.current.fields : new Set<string>();
    const activeFields = new Set<string>();
    const sectionsToReveal = new Set<string>();

    nestedSectionFields.forEach(({ section, fields }) => {
      if (!updatedApplicationAssetSectionsList.some(({ id }) => id === section)) return;

      fields.forEach((fieldName) => {
        if (!getLocalAndLenderError({ fieldName })) return;
        activeFields.add(fieldName);
        if (validationTriggered || !previousFields.has(fieldName)) sectionsToReveal.add(section);
      });
    });

    previousNestedErrors.current = { assetId: _id, fields: activeFields, validationErrors: assetValidationErrors };
    if (!sectionsToReveal.size) return;

    setAssetExpanded(_id);
    sectionsToReveal.forEach((section) => {
      if (!expandedSections.includes(section)) {
        dispatch(setAssetExpandedSection({ assetId: _id, section, isExpanded: true }));
      }
    });
  });

  /** Specification key already written to this asset. */
  const appliedSpecificationRef = useRef<string | null>(null);

  // These permissions gate the cost of a billed lookup, so AutoGrab uses the same gate.
  const isGlassesGuideMode = isGlassesGuideAssetMode({
    assetType: allValues?.assetTypeValue,
    isManualAssets,
    isGlassesGuideSearchable,
  });
  const canLookUpVehicle =
    Boolean(canUsePaidServices) && Boolean(servicePermission?.blug_flag || servicePermission?.blug_flag_ppsr);
  const canEnterRegistration = canLookUpVehicle && Boolean(isVehicleSearch);
  const canEnterVin = canLookUpVehicle && Boolean(isPPSRSearch);

  const isSearchView = !isManualAssets && showSearchAssetView === "search";
  const showPlainTextSearch = isGlassesGuideMode && isSearchView && isAutograbLookup;
  const isAwaitingLookupProvider = isGlassesGuideMode && isSearchView && !isAssetLookupProviderSettled;
  const showRegistrationSearch =
    isSearchView &&
    searchableAssets.includes(allValues?.assetTypeValue) &&
    (isVehicleSearch || isPPSRSearch) &&
    Boolean(servicePermission?.blug_flag || servicePermission?.blug_flag_ppsr);

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    dispatch(setAssetExpandedSection({ assetId: _id, section: panel, isExpanded }));
  };

  const handleAssetExpand = (id: string) => {
    setAssetExpanded((prev) => (prev === id ? null : (id as string)));
  };

  const AccordionIcon = ({ isExpanded, isMainItem }: { isExpanded: boolean; isMainItem: boolean }) => (
    <Box
      sx={{
        width: 25,
        height: 25,
        backgroundColor: isExpanded ? theme.palette.primary[200] : "#e2e8f0",
        color: isExpanded ? "white" : "#64748b",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mr: 5,
        left: -2,
        position: "relative",
      }}
    >
      <ExpandMoreIcon
        sx={{
          fontSize: 20,
          transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.2s",
        }}
      />
    </Box>
  );

  const validationSchema = Yup.object({
    assetValue: Yup.string()
      .required(fieldRequiredMessage.assetValue)
      .matches(regex.allowOnlyNumber, "Only numbers and decimal points allowed.")
      .matches(regex.allowNumberAndDecimal, {
        message: "Number must be to up 7 digit and up to 2 decimal places",
      })
      .min(3, "Minimum of 3 digits")
      .max(10, "Maximum of 7 digits"),
    model: Yup.string()
      .optional()
      .matches(regex.alphaNumericAndSpecificChar, "Only letters, numbers and . ! / ( ) - + characters allowed.")
      .max(50, "Maximum of 50 characters"),
    make: Yup.string()
      .optional()
      .matches(regex.alphaNumericAndSpecificChar, "Only letters, numbers and . ! / ( ) - + characters allowed.")
      .max(50, "Maximum of 50 characters"),
  });

  const debouncedValidation = useCallback(
    debounce(async (fieldName, value) => {
      try {
        await validationSchema.validateAt(fieldName, { [fieldName]: value });
        if (assetErrors) {
          dispatch(
            setValidationRequireFieldError({
              ...userDetails,
              assetErrors: { ...assetErrors, [fieldName]: "" },
            }),
          );
        }
      } catch (error) {
        if (assetErrors) {
          dispatch(
            setValidationRequireFieldError({
              ...userDetails,
              assetErrors: { ...assetErrors, [fieldName]: (error as Error).message },
            }),
          );
        }
      }
    }, 300),
    [userDetails, assetErrors],
  );

  const shouldFetchList = useCascadeListFetch();

  /** Cascade fetches are fire-and-forget, so each reports its own failure. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatchCascadeList = async (thunkAction: any) => {
    const res: IDispatchResponse = await dispatch(thunkAction);
    if (res?.error) dispatch(openSnackbar(getErrorToastPayload(res, ERRORS_MESSAGE.fetchErrorMsg)));
  };

  const handle = {
    getGlassesFieldValue: (field: string) => {
      const val = allValues[field as keyof typeof allValues];
      return typeof val === "string" ? val : (val as { value?: string })?.value || "";
    },
    updateAssetModalCode: (value: string) => {
      const findModalObject = assetTypesList?.find(
        (el: { label: string; modalTypeCode: string }) => el.label === value,
      );
      const updateValue = {
        ...allValues,
        assetTypeValue: value || findModalObject?.label,
        assetModelTypeCode: findModalObject?.modalTypeCode,
      };
      setAllValue(updateValue);

      return updateValue;
    },
    ageOfAsset: (newValue: string) => {
      const termInYears = term ? (term as number) / 12 : 0;
      const ageOfAssetAtEnd = moment().add(termInYears, "years").diff(moment(newValue, "YYYY"), "years");
      return ageOfAssetAtEnd;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChangeField: (e: any, name: string) => {
      let isValid = true;

      if (name === "assetTypeValue") {
        const dataSetting = handle.updateAssetModalCode(e);

        const updateValue = {
          ...(isDefaultManualAssets && { isManualAssets: true }),
          condition: typeOfSale && typeOfSale === "Private sale" ? "Used" : condition || "",
          specifications: null,
          glassesMake: { label: "", value: "" },
          glassesModel: { label: "", value: "" },
          glassesVariant: { label: "", value: "" },
          glassesNVIC: { label: "", value: "" },
        };

        handle.assetFieldsView(e);

        dispatch(
          updateGlassAsset({
            applicationId,
            ...asset,
            ...updateValue,
          }),
        );

        setAllValue({
          ...allValues,
          ...updateValue,
          [name]: e,
          assetModelTypeCode: dataSetting.assetModelTypeCode,
        });

        // Reset the invalid required fields by field name.
        resetInvalidRequiredFieldsByFieldName({
          section: "asset",
          // For assetTypeValue, we need to reset the assetType field, it is named as assetTypeValue
          // in the local state which is not the same as the assetType field in the backend.
          fieldName: name === "assetTypeValue" ? "assetType" : name,
        });

        // Expand asset insurance section if the asset type is an insurance quote asset
        if (
          !expandedSections.includes(ApplicationAssetSections.INSURANCE_QUOTE) &&
          INSURANCE_QUOTE_ASSETS.includes(e)
        ) {
          handleAccordionChange(ApplicationAssetSections.INSURANCE_QUOTE)({} as SyntheticEvent, true);
        }

        return dispatch(
          updateAsset({
            applicationId,
            ...updateValue,
            assetType: e,
            _id,
          }),
        );
      }

      // Asset fields validation
      if (name === "model" || name === "make") {
        isValid = regex.alphaNumericAndSpecificChar.test(e) && e?.length <= 50;
      } else if (name === "assetValue") {
        isValid = isValidInput(e);
      }

      if (name === "typeOfSale") {
        const updateValue = {
          typeOfSale: e,
          ...(e === "Private sale" && { condition: "Used" }),
        };

        setAllValue({
          ...allValues,
          ...updateValue,
        });

        dispatch(
          updateGlassAsset({
            ...asset,
            ...updateValue,
          }),
        );

        return dispatch(
          updateAsset({
            applicationId,
            ...updateValue,
            _id,
          }),
        );
      }

      if (name === "condition") {
        setAllValue({
          ...allValues,
          [name]: e,
        });

        return dispatch(
          updateAsset({
            applicationId,
            [name]: e,
            _id,
          }),
        );
      }

      if (name === "ageOfAsset") {
        dispatch(updateAgeOfAsset(e));
        const updateValue = {
          glassesData: {},
          specifications: null,
          glassesMake: { label: "", value: "" },
          glassesModel: { label: "", value: "" },
          glassesVariant: { label: "", value: "" },
          glassesNVIC: { label: "", value: "" },
          listOfMakes: [],
          listOfModels: [],
          listOfVariants: [],
          NVICList: [],
        };

        setAllValue({
          ...allValues,
          ...updateValue,
          [name]: e,
        });

        dispatch(
          updateGlassAsset({
            ...asset,
            ...updateValue,
            ageOfAsset: e,
          }),
        );
        return dispatch(
          updateAsset({
            applicationId,
            _id,
            ageOfAsset: e,
            ageOfAssetAtEnd: handle.ageOfAsset(e),
            ...updateValue,
          }),
        );
      }

      // Glasses fields validation
      if (name === "glassesMake") {
        const updateValue = {
          [name]: e,
          glassesData: {},
          specifications: null,
          glassesModel: { label: "", value: "" },
          glassesVariant: { label: "", value: "" },
          glassesNVIC: { label: "", value: "" },
          // new added
          listOfModels: [],
          listOfVariants: [],
          NVICList: [],
        };

        setAllValue({
          ...allValues,
          ...updateValue,
        });

        dispatch(
          updateGlassAsset({
            ...asset,
            ...updateValue,
          }),
        );

        return dispatch(
          updateAsset({
            applicationId,
            _id,
            ...updateValue,
          }),
        );
      }

      if (name === "glassesModel") {
        setAllValue({
          ...allValues,
          [name]: e,
          glassesData: {},
          specifications: null,
          glassesVariant: { label: "", value: "" },
          glassesNVIC: { label: "", value: "" },
        });

        dispatch(
          updateGlassAsset({
            ...asset,
            [name]: e,
            glassesData: {},
            specifications: null,
            glassesVariant: null,
            glassesNVIC: { label: "", value: "" },
            listOfVariants: [],
            NVICList: [],
          }),
        );

        return dispatch(
          updateAsset({
            applicationId,
            [name]: e,
            specifications: null,
            glassesVariant: null,
            glassesNVIC: { label: "", value: "" },
            listOfVariants: [],
            NVICList: [],
            _id,
          }),
        );
      }

      if (name === "glassesVariant") {
        setAllValue({
          ...allValues,
          [name]: e === null ? { label: "", value: "" } : e,
          glassesData: {},
          specifications: null,
          glassesNVIC: { label: "", value: "" },
        });

        dispatch(
          updateGlassAsset({
            ...asset,
            [name]: e,
            glassesData: {},
            specifications: null,
            glassesNVIC: { label: "", value: "" },
            NVICList: [],
          }),
        );

        return dispatch(
          updateAsset({
            applicationId,
            [name]: e,
            specifications: null,
            glassesNVIC: { label: "", value: "" },
            NVICList: [],
            _id,
          }),
        );
      }

      // Supplier fields validation
      if (name === "supplierName" || name === "supplierContactName") {
        isValid = regex.alphaNumericAndSpecificChar.test(e) && e?.length <= 50;
      } else if (name === "supplierABN") {
        isValid = e === "" || (regex.allowStrictNumber.test(e) && e?.length <= 12);
      } else if (name === "supplierAddress") {
        isValid = e === "" || (regex.addressRegex.test(e) && e?.length <= 100);
      } else if (name === "supplierEmail") {
        isValid = e?.length <= 50;
      }

      if (!isValid) {
        debouncedValidation(name, e);
        return;
      } else {
        const updatedValues = {
          ...allValues,
          [name]: e,
        };
        setAllValue(updatedValues);
        debouncedValidation(name, e);
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateAsset: async (value: any) => {
      if (_id) {
        const keys = Object.keys(value);
        for (let i = 0; i < keys.length; i++) {
          const key = keys[i];
          if (asset[key as keyof Asset] !== value[key]) {
            dispatch(updateAsset({ applicationId, _id, ...value }));
            return;
          }
        }
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    blurFn: async (fieldName: string, value: any) => {
      try {
        if (assetFieldErrors[fieldName] === "" || fieldName) {
          if (fieldName === "assetValue") {
            // We need to update and save the net asset value when the fields that impact it are changed.
            // Impacted fields are: asset.assetValue, loanDetails.deposit, loanDetails.tradeIn, loanDetails.payout.
            const netAssetValue = getNetAssetValueProvidedSingleField({ fieldName, fieldValue: value });
            handle.updateAsset({
              [fieldName]: value,
              netAssetValue,
            });
          } else {
            handle.updateAsset({ [fieldName]: value });
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        const newErrors: Record<string, string> = {};
        error.inner.forEach((validationError: { path: string | number; message: string }) => {
          newErrors[validationError.path] = validationError.message || "";
        });
        setAssetFieldErrors(newErrors);
      }
    },
    onChangeAddOns: (index: number, field: keyof AddOnsProps, value: string | number) => {
      const newRows = [...allValues.addOns]; // shallow copy array
      const updatedRow = { ...newRows[index] }; // clone the row object

      if (field === "price") {
        updatedRow[field] = Number(value);
      } else {
        updatedRow[field] = value as string;
      }

      newRows[index] = updatedRow; // replace with updated object
      setAllValue({ ...allValues, addOns: newRows });
    },
    onRemoveAddOns: (index: number) => {
      const updatedList = allValues.addOns.filter((row: AddOnsProps, i: number) => i !== index);
      setAllValue({ ...allValues, addOns: updatedList || [] });
      handle.updateAsset({ addOns: updatedList || [] });
    },
    assetFieldsView: (assetTypeValue: string) => {
      const dataSetting = handle.updateAssetModalCode(assetTypeValue);

      if (searchableAssets.includes(assetTypeValue)) {
        setAllValue((prevAllValues) => {
          const newAllValues = {
            ...prevAllValues,
            condition: typeOfSale === "Private sale" ? "Used" : "",
            assetTypeValue,
            ageOfAsset: "",
            glassesData: {},
            glassesMake: { label: "", value: "" },
            glassesModel: { label: "", value: "" },
            glassesVariant: { label: "", value: "" },
            glassesNVIC: { label: "", value: "" },
            assetModelTypeCode: dataSetting.assetModelTypeCode,
          };

          allValuesRef.current = newAllValues;
          return newAllValues;
        });
        handle.getListOfMakes(ageOfAsset, dataSetting.assetModelTypeCode);

        return setShowSearchAssetView("search");
      }

      setShowSearchAssetView("manual");
    },
    getListOfMakes: async (yearValue: string, assetCode: string) => {
      const hasYear = Boolean(yearValue || ageOfAsset);
      const assetTypeCode = assetCode || allValues?.assetModelTypeCode || "APAS";
      const request = hasYear
        ? { assetId: _id, assetTypeCode, year: yearValue || ageOfAsset || allValues?.ageOfAsset }
        : { assetId: _id, assetTypeCode, from: LIST_YEAR_FROM, to: listYearTo() };

      if (!shouldFetchList("listOfMakes", request, Boolean(asset?.makeLoader), Boolean(asset?.listOfMakes?.length))) {
        return;
      }

      await dispatchCascadeList(hasYear ? getListOfMakes(request) : getListOfMakesYearFromTo(request));
    },
    getListOfModels: async (makeCode: string, assetCode: string) => {
      const hasYear = Boolean(allValues?.ageOfAsset);
      const common = {
        assetId: _id,
        makeCode: makeCode || handle.getGlassesFieldValue("glassesMake"),
        assetTypeCode: assetCode || allValues?.assetModelTypeCode || "APAS",
      };
      const request = hasYear
        ? { ...common, year: allValues?.ageOfAsset }
        : { ...common, from: LIST_YEAR_FROM, to: listYearTo() };

      if (
        !shouldFetchList("listOfModels", request, Boolean(asset?.modelLoader), Boolean(asset?.listOfModels?.length))
      ) {
        return;
      }

      await dispatchCascadeList(hasYear ? getListOfModels(request) : getListOfModelsYearFromTo(request));
    },
    getListOfVariants: async (modelCode: string, assetCode: string) => {
      const hasYear = Boolean(allValues?.ageOfAsset);
      const common = {
        assetId: _id,
        assetTypeCode: assetCode || allValues?.assetModelTypeCode || "APAS",
        makeCode: handle.getGlassesFieldValue("glassesMake"),
        modelCode: modelCode || handle.getGlassesFieldValue("glassesModel"),
      };
      const request = hasYear
        ? { ...common, year: allValues?.ageOfAsset }
        : { ...common, from: LIST_YEAR_FROM, to: listYearTo() };

      if (
        !shouldFetchList(
          "listOfVariants",
          request,
          Boolean(asset?.variantLoader),
          Boolean(asset?.listOfVariants?.length),
        )
      ) {
        return;
      }

      await dispatchCascadeList(hasYear ? getListOfVariants(request) : getListOfVariantsYearFromTo(request));
    },
    getNVIC: async (variant: string, assetCode: string) => {
      const request = {
        assetId: _id,
        assetTypeCode: assetCode || allValues?.assetModelTypeCode || "APAS",
        yearCreate: allValues?.ageOfAsset,
        makeCode: handle.getGlassesFieldValue("glassesMake"),
        modelCode: handle.getGlassesFieldValue("glassesModel"),
        variant: variant || handle.getGlassesFieldValue("glassesVariant"),
      };

      if (!shouldFetchList("NVICList", request, Boolean(asset?.NVICLoader), Boolean(asset?.NVICList?.length))) return;

      await dispatchCascadeList(getListOfNVICForAdvancedSearch(request));
    },
    searchVehiclesByKeyword: (keyword: string) => {
      dispatch(
        searchVehiclesByKeyword({
          assetId: _id,
          assetTypeCode: allValues?.assetModelTypeCode || "APAS",
          keyword,
        }),
      );
    },
    /** Glasses' specification for a picked NVIC. */
    getDetailsSpecificationAll: async (NVIC: string) => {
      const modelSpecificationCode = assetTypesList.find(
        (item: AssetTypesJsonProps) => item.label === allValues?.assetTypeValue,
      )?.modelSpecificationCode;
      await dispatch(
        getDetailsSpecificationAll({
          assetId: _id,
          assetTypeCode: allValues?.assetModelTypeCode || "A",
          modelSpecificationCode: modelSpecificationCode || "A",
          NVIC,
        }),
      ).then((res: IDispatchResponse) => {
        if (res?.error) dispatch(openSnackbar(getErrorToastPayload(res, ERRORS_MESSAGE.fetchErrorMsg)));
      });
    },
    /** AutoGrab's specification and valuation for a picked vehicle id. Billed, so it is bought once. */
    getVehicleSpecification: async (vehicleId: string) => {
      await dispatch(getVehicleSpecification({ assetId: _id, applicationId, vehicleId })).then(
        (res: IDispatchResponse) => {
          if (res?.error) dispatch(openSnackbar(getErrorToastPayload(res, ERRORS_MESSAGE.fetchErrorMsg)));
        },
      );
    },
    // Handles vehicle selection/clearing and fetches specifications.
    applyVehicleSelection: async (option: { NVIC?: string | null; label?: string | null } | null) => {
      const label = option?.label || "";
      const glassesNVIC = { label, value: label };

      handle.onChangeField(glassesNVIC, "glassesNVIC");

      // AutoGrab persists on spec fetch; only blur-save on clear or for other providers.
      if (!isAutograbLookup || !option?.NVIC) handle.blurFn("glassesNVIC", glassesNVIC);

      if (!option?.NVIC) return;

      // Reset ref to force re-applying specifications.
      appliedSpecificationRef.current = null;

      if (isAutograbLookup) {
        await handle.getVehicleSpecification(option.NVIC);
        return;
      }

      await handle.getDetailsSpecificationAll(option.NVIC);
    },
    getVehicleLookup: (query: VehicleLookupQuery) => {
      // Re-running the lookup is an explicit ask to re-apply.
      appliedSpecificationRef.current = null;
      return dispatch(getVehicleLookup(buildVehicleLookupQuery(query, applicationId, _id))).then(
        (res: IDispatchResponse) => {
          dispatch(openSnackbar(vehicleLookupResultSnackbar(res)));
        },
      );
    },
    getVehiclePPSRSearch: (query: VehicleLookupQuery) =>
      dispatch(getVehiclePPSRSearch(buildVehicleLookupQuery(query, applicationId, _id))).then(
        (res: IDispatchResponse) => {
          dispatch(openSnackbar(vehicleLookupResultSnackbar(res)));
        },
      ),
    addAddOn: (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      const addOnsPayload = {
        ...allValues,
        addOns: [...allValues.addOns, { description: "", price: 0 }],
      };
      setAllValue(addOnsPayload);

      handle.updateAsset({ addOns: addOnsPayload.addOns });
    },
    handleToggleAssetDetails: () => {
      // setToggleManualAsset(!toggleManualAsset);
      setAllValue({
        ...allValues,
        isManualAssets: !isManualAssets,
        listOfModels: [],
        listOfVariants: [],
        NVICList: [],
        glassesData: {},
        specifications: null,
        glassesMake: { label: "", value: "" },
        glassesModel: { label: "", value: "" },
        glassesVariant: { label: "", value: "" },
        glassesNVIC: { label: "", value: "" },
      });

      dispatch(
        updateGlassAsset({
          ...asset,
          isManualAssets: !isManualAssets,
          listOfModels: [],
          listOfVariants: [],
          NVICList: [],
          glassesData: {},
          specifications: null,
          glassesMake: { label: "", value: "" },
          glassesModel: { label: "", value: "" },
          glassesVariant: { label: "", value: "" },
          glassesNVIC: { label: "", value: "" },
        }),
      );

      setShowSearchAssetView(!isManualAssets ? "manual" : "search");

      return dispatch(
        updateAsset({
          applicationId,
          _id,
          isManualAssets: !isManualAssets,
          listOfModels: [],
          listOfVariants: [],
          NVICList: [],
          glassesData: {},
          specifications: null,
          glassesMake: { label: "", value: "" },
          glassesModel: { label: "", value: "" },
          glassesVariant: { label: "", value: "" },
          glassesNVIC: { label: "", value: "" },
        }),
      );
    },
  };

  /**
   * Maps a specification payload onto the asset's fields. `persist` writes them back: AutoGrab stores
   * its own on the server, so there the asset only has to catch up locally.
   */
  const applySpecificationPayload = (
    specification: Specifications | Record<string, unknown> | null | undefined,
    persist: boolean,
  ) => {
    const NVICData = specification as Specifications & {
      ManufacturerCode?: string;
      FamilyCode?: string;
      assetLookupSource?: string;
      vehicleId?: string;
    };

    if (NVICData?.YearCreate) {
      const specData: {
        ageOfAsset?: string;
        ageOfAssetAtEnd?: string | number;
        glassesMake?: { label?: string; value?: string } | string | null;
        glassesModel?: { label?: string; value?: string } | string | null;
        glassesVariant?: { label?: string; value?: string } | string | null;
        glassesNVIC?: { label?: string; value?: string } | string | null;
        specifications?: Specifications;
        assetLookupSource?: string;
        vehicleId?: string;
        vin?: string;
      } = {};
      specData.ageOfAsset = NVICData?.YearCreate;
      specData.ageOfAssetAtEnd = handle.ageOfAsset(NVICData?.YearCreate);
      // The payload's own codes first, so the store matches what the server stored: AutoGrab's cascade
      // options are their own code, and the picked make would otherwise match no option in the list.
      specData.glassesMake = {
        label: NVICData?.ManufacturerName || "",
        value:
          NVICData?.ManufacturerCode ||
          (typeof allValues?.glassesMake === "object" ? allValues?.glassesMake?.value || "" : ""),
      };
      specData.glassesModel = {
        label: NVICData?.FamilyName,
        value: NVICData?.FamilyCode || handle.getGlassesFieldValue("glassesModel"),
      };
      specData.glassesVariant =
        typeof allValues?.glassesVariant === "object"
          ? {
              label: NVICData?.VariantName || allValues?.glassesVariant?.label || "",
              value: NVICData?.VariantName || allValues?.glassesVariant?.value || "",
            }
          : {
              label: NVICData?.VariantName || "",
              value: NVICData?.VariantName || "",
            };

      if (glassesNvicUpdate()) {
        specData.glassesNVIC =
          typeof allValues?.glassesNVIC === "object"
            ? {
                label: NVICData?.ModelName || allValues?.glassesNVIC?.label || "",
                value: NVICData?.ModelName || allValues?.glassesNVIC?.value || "",
              }
            : {
                label: NVICData?.ModelName || "",
                value: NVICData?.ModelName || "",
              };
      } else {
        specData.glassesNVIC = NVICData?.ModelName;
      }
      specData.specifications = NVICData;
      const lookupSource = NVICData?.assetLookupSource;
      if (lookupSource) specData.assetLookupSource = lookupSource;
      // Kept apart from `glassesNVIC` so it is not mistaken for a real NVIC.
      const lookupVehicleId = NVICData?.vehicleId;
      if (lookupVehicleId) specData.vehicleId = lookupVehicleId;
      const lookupVin = readSpecificationVin(specification as Record<string, unknown>);
      if (lookupVin) specData.vin = lookupVin;

      // Normalised once so the store and the local fields agree: `glassesNVIC` arrives as a bare string
      // with `glassesNvicUpdate` off, and the pickers read it back as an option.
      const normalisedSpecData = {
        ...specData,
        glassesMake: toGlassesOption(specData.glassesMake),
        glassesModel: toGlassesOption(specData.glassesModel),
        glassesVariant: toGlassesOption(specData.glassesVariant),
        glassesNVIC: toGlassesOption(specData.glassesNVIC),
      };

      const assetPayload = { ...allValues, ...normalisedSpecData };

      setAllValue(assetPayload);

      handleAccordionChange(ApplicationAssetSections.ASSET_DETAILS)({} as SyntheticEvent, true);

      if (persist) {
        handle.updateAsset(assetPayload);
        return;
      }

      // Already stored server-side, so the asset only needs the same fields in the store to render them.
      dispatch(updateGlassAsset({ ...asset, ...normalisedSpecData }));
    }
  };

  /** Applies a payload once per vehicle, so a re-rendered asset is not written again. */
  const applySpecificationOnce = (specification: Record<string, unknown> | null | undefined) => {
    const specificationKey = getSpecificationKey(specification);
    if (!specificationKey || appliedSpecificationRef.current === specificationKey) return;

    appliedSpecificationRef.current = specificationKey;
    applySpecificationPayload(specification, !isAutograbLookup);
  };

  useEffect(() => {
    // Applying before the provider settles would save an AutoGrab asset through the Glasses branch,
    // and the applied-once guard would keep it that way.
    if (!isAssetLookupProviderSettled) return;

    applySpecificationOnce(NVICData);
  }, [NVICData, isAutograbLookup, isAssetLookupProviderSettled]);

  useEffect(() => {
    if (!isAssetLookupProviderSettled) return;
    if (!vehicleData || typeof vehicleData !== "object") return;

    // AutoGrab answers with the whole record; Glasses answers an NVIC to resolve.
    if (isAutograbLookup) {
      applySpecificationOnce(vehicleData);
      return;
    }

    if (vehicleData.nvic) {
      handle.getDetailsSpecificationAll(vehicleData.nvic);
    }
  }, [vehicleData, isAutograbLookup, isAssetLookupProviderSettled]);

  useEffect(() => {
    dispatch(getAssetLookupProvider());
  }, [dispatch]);

  useEffect(() => {
    if (searchableAssets.includes(allValues.assetTypeValue) && !isManualAssets) {
      return setShowSearchAssetView("search");
    }
    setShowSearchAssetView("manual");
  }, [_id]);

  return (
    <>
      <Accordion
        key={_id}
        id={`asset-${_id}`}
        expanded={assetExpanded === _id}
        onChange={() => handleAssetExpand(_id)}
        disableGutters
        sx={{
          "&:before": { display: "none" },
          boxShadow: "none",
          ...(assetExpanded === _id && {
            backgroundColor: "#ffffff",
          }),
        }}
      >
        <AccordionSummary
          sx={{
            "& .MuiAccordionSummary-content": {
              justifyContent: "space-between",
              alignItems: "center",
            },
            backgroundColor: assetExpanded === _id ? "#FBFBFB" : "#ffffff",
            borderRadius: "20px 20px 0 0",
          }}
        >
          <AccordionIcon isExpanded={assetExpanded === _id} isMainItem={true} />
          <Grid container alignItems="center">
            <Grid item xs={12} md={9} lg={9}>
              <Stack direction="row" alignItems="center" gap={6}>
                <Typography
                  fontWeight={assetExpanded === _id ? 700 : 500}
                  level={assetExpanded === _id ? "title-sm" : "body-md"}
                >
                  {asset?.assetType || "-"}
                </Typography>
                <Stack
                  direction="row"
                  gap={3}
                  style={{ backgroundColor: "#e0e0e0", padding: "0 10px", borderRadius: "5px" }}
                >
                  <Typography>{asset?.vin || asset?.registrationNumber || "-"}</Typography>
                  <Typography>|</Typography>
                  <Typography>{asset.assetValue ? fCurrency(asset.assetValue) : "$0.00"}</Typography>
                </Stack>
              </Stack>
            </Grid>

            <Grid item xs={12} md={3} lg={3} onClick={(e) => e.stopPropagation()}>
              <AssetActions
                index={index}
                asset={asset as Asset}
                applicationId={applicationId}
                isDisableEdit={isDisableEdit}
              />
            </Grid>
          </Grid>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            px: 3,
            py: 2,
            position: "relative",
            backgroundColor: assetExpanded === _id ? "#FBFBFB" : "#ffffff",
            borderRadius: "0 0 20px 20px",
          }}
        >
          {/* vertical connector line */}
          <Box
            sx={{
              position: "absolute",
              top: -17,
              bottom: 16,
              width: "2px",
              bgcolor: "#e2e8f0",
            }}
          />

          <Grid container alignItems="center" style={{ borderBottom: "2px solid #eee" }}>
            <Grid item xs={0.5}></Grid>
            <Grid container item xs={11.5} spacing={showSearchAssetView === "search" ? 3 : 0}>
              <Grid item xs={12} sm={12} md={12}>
                <AssetDealDetails
                  assetId={_id}
                  assetTypesList={assetTypesList}
                  searchVehicleLoader={false}
                  allValues={allValues}
                  handle={handle}
                  getLocalAndLenderError={getLocalAndLenderError}
                  isDisableEdit={isDisableEdit}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12}>
                {isAwaitingLookupProvider ? (
                  <Loader text={ASSET_SEARCH_OPTIONS.LOADING} height="240px" />
                ) : (
                  <AssetSearchOptions>
                    {showPlainTextSearch && (
                      <AssetSearchOption title={PLAIN_TEXT_VEHICLE_SEARCH.HEADING}>
                        <PlainTextVehicleSearch asset={asset} handle={handle} isDisableEdit={isDisableEdit} />
                      </AssetSearchOption>
                    )}

                    {showRegistrationSearch && (
                      <AssetSearchOption title={VEHICLE_LOOKUP_SEARCH.HEADING}>
                        <SearchVehicle
                          asset={asset}
                          allValues={allValues}
                          canUsePaidServices={canUsePaidServices}
                          servicePermission={servicePermission}
                          fieldErrors={assetFieldErrors}
                          getLocalAndLenderError={getLocalAndLenderError}
                          handle={handle}
                          isVehicleSearch={isVehicleSearch}
                          isPPSRSearch={isPPSRSearch}
                          isDisableEdit={isDisableEdit}
                        />
                      </AssetSearchOption>
                    )}

                    {isGlassesGuideMode && (
                      <AssetSearchOption title={ASSET_SEARCH_OPTIONS.MAKE_MODEL_HEADING}>
                        <AssetMakeModelSearch
                          asset={asset}
                          allValues={allValues}
                          handle={handle}
                          getLocalAndLenderError={getLocalAndLenderError}
                          isDisableEdit={isDisableEdit}
                        />
                      </AssetSearchOption>
                    )}
                  </AssetSearchOptions>
                )}
              </Grid>

              <Grid item xs={12} sm={12} md={12} style={{ paddingTop: 0 }}>
                <GlassesGuideFields
                  asset={asset}
                  allValues={allValues}
                  handle={handle}
                  isManualAssets={isManualAssets}
                  isGlassesGuideSearchable={isGlassesGuideSearchable}
                  getLocalAndLenderError={getLocalAndLenderError}
                  isDisableEdit={isDisableEdit}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Nested sections */}
          {updatedApplicationAssetSectionsList.map((section) => (
            <Accordion
              key={section.id}
              disableGutters
              expanded={expandedSections.includes(section.id)}
              onChange={handleAccordionChange(section.id)}
              sx={{
                backgroundColor: "transparent",
                boxShadow: "none",
                "&:before": { display: "none" },
                mb: 1,
                borderBottom: "1px solid #eee",
              }}
            >
              <AccordionSummary>
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: "47%",
                    width: 25,
                    height: 2,
                    backgroundColor: "#e2e8f0",
                  }}
                />
                <Grid container alignItems="center">
                  <Grid item xs={0.5}>
                    <AccordionIcon isExpanded={expandedSections.includes(section.id)} isMainItem={false} />
                  </Grid>
                  <Grid item xs={9.5}>
                    <Typography level="title-sm" fontWeight={600}>
                      {section.title}
                    </Typography>
                  </Grid>
                  {section.id === ApplicationAssetSections.ADDONS &&
                    expandedSections.includes(ApplicationAssetSections.ADDONS) && (
                      <Grid item xs={2} sm={2} md={2}>
                        <Stack alignItems="flex-end">
                          <Button variant="secondary" size="sm" onClick={handle.addAddOn} disabled={isDisableEdit}>
                            + New Add-on
                          </Button>
                        </Stack>
                      </Grid>
                    )}
                </Grid>
              </AccordionSummary>
              <AccordionDetails>
                <Stack
                  width="100%"
                  direction="column"
                  alignItems="stretch"
                  alignContent="stretch"
                  rowGap={3}
                  sx={{ pl: "35px" }}
                >
                  {section.id === ApplicationAssetSections.INSURANCE_QUOTE && (
                    <InsuranceQuote asset={asset} customer={firstCustomer} applicationId={applicationId} />
                  )}
                  {/* AutoGrab stamps a vehicle id and returns a payload the Glasses panel cannot read. */}
                  {section.id === ApplicationAssetSections.ASSET_DETAILS &&
                    (asset?.vehicleId ? (
                      <AssetSpecificationDetails asset={asset} loanDetails={loanDetails as LoanDetails} />
                    ) : (
                      <GlassesGuideDetails asset={asset} loanDetails={loanDetails as LoanDetails} />
                    ))}
                  {section.id === ApplicationAssetSections.ODOMETER && (
                    <Odometer
                      asset={asset}
                      allValues={allValues}
                      handle={handle}
                      getLocalAndLenderError={getLocalAndLenderError}
                      isDisableEdit={isDisableEdit}
                    />
                  )}
                  {section.id === ApplicationAssetSections.ADDONS && (
                    <AssetAddOns assetAddOns={allValues.addOns ?? []} handle={handle} isDisableEdit={isDisableEdit} />
                  )}
                  {section.id === ApplicationAssetSections.INVOICE && (
                    <AssetInvoice
                      asset={asset}
                      applicationId={applicationId}
                      isDisableEdit={isDisableEdit}
                      isGlassesGuideMode={isGlassesGuideMode}
                      canEnterRegistration={canEnterRegistration}
                      canEnterVin={canEnterVin}
                    />
                  )}
                  {section.id === ApplicationAssetSections.SUPPLIER && (
                    <AssetSupplier
                      handle={handle}
                      applicationId={applicationId}
                      allValues={allValues}
                      getLocalAndLenderError={getLocalAndLenderError}
                      isDisableEdit={isDisableEdit}
                    />
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default AssetSections;
